import { computed, reactive, ref, watch } from 'vue'
import type { ClassicKeyTravel, KeyTravelList } from '@rapoo/device-sdk'
import type { PresetTravelValues } from '../useItineraryDevice'

/* -------- 默认值常量 -------- */
const DEFAULT_KEY_TRAVEL: ClassicKeyTravel = {
  absoluteTrigger: 135, // 绝对触发位置 1.35mm
  rtTriggerSensitivity: 50, // RT 快速触发灵敏度 0.50mm
  rtResetSensitivity: 50, // RT 快速触发重置灵敏度 0.50mm
  rtTriggerEnable: false, // RT 快速触发开关
  absoluteTriggerEnable: true, // 绝对触发 开关
  bottomDeadZone: 0, // 单键底部死区 0.00mm
}

/**
 * 行程功能的业务 UI 状态（原 useItineraryState 的实例化版本）。
 *
 * - 无模块级单例：每次调用创建独立状态与 watch；
 * - 需在页面最外层调用一次，再通过 provide / inject 向下传递。
 */
export function useItineraryModel() {
  // 所有按键的 KeyTravel 状态，key = 键位索引 index
  const keyTravelMap = reactive(new Map<number, ClassicKeyTravel>())

  // 当前选中的按键索引
  const selectedKeys = ref<{ index: number }[]>([])

  // RT 切换键索引：0=≈0.5mm, 1=≈1.0mm, 2=≈1.5mm, 3=自定义
  const rtSwitchIndex = ref(3)

  // 三个预设值（可被 slider 拖动修改）
  const presetValues = reactive<PresetTravelValues[]>([
    { rtTriggerSensitivity: 50, rtResetSensitivity: 50, bottomDeadZone: 0 },
    { rtTriggerSensitivity: 100, rtResetSensitivity: 100, bottomDeadZone: 0 },
    { rtTriggerSensitivity: 150, rtResetSensitivity: 150, bottomDeadZone: 0 },
  ])

  // 对等模式
  const isRelated = ref(true)

  // 当前编辑视图状态（UI 表单绑定，代表当前选中按键的编辑值）
  const editingState = reactive<ClassicKeyTravel>({ ...DEFAULT_KEY_TRAVEL })

  // 内部标记：防止 watch 循环
  let syncing = false

  /* -------- 工具函数 -------- */

  const getKeyTravel = (index: number): ClassicKeyTravel => {
    return keyTravelMap.get(index) ?? { ...DEFAULT_KEY_TRAVEL }
  }

  const setKeyTravel = (index: number, travel: Partial<ClassicKeyTravel>) => {
    const existing = keyTravelMap.get(index) ?? { ...DEFAULT_KEY_TRAVEL }
    keyTravelMap.set(index, { ...existing, ...travel })
  }

  /** 初始化所有按键到 keyTravelMap（确保 map 不为空） */
  const initAllKeys = (indices: number[]) => {
    for (const index of indices) {
      if (!keyTravelMap.has(index)) {
        keyTravelMap.set(index, { ...DEFAULT_KEY_TRAVEL })
      }
    }
  }

  /** 将 editingState 同步到所有选中按键 */
  const syncEditingToSelected = () => {
    for (const { index } of selectedKeys.value) {
      setKeyTravel(index, { ...editingState })
    }
  }

  /** 从第一个选中按键加载状态到 editingState；rtTriggerEnable 取所有选中按键的交集 */
  const loadEditingFromSelected = () => {
    if (!selectedKeys.value.length) return

    const first = selectedKeys.value[0]
    const travel = getKeyTravel(first.index)

    // rtTriggerEnable：所有选中按键都开启才显示开启
    const allRtEnabled = selectedKeys.value.every(({ index }) => {
      return getKeyTravel(index).rtTriggerEnable
    })

    syncing = true
    Object.assign(editingState, travel)
    editingState.rtTriggerEnable = allRtEnabled
    syncing = false
  }

  /** 包裹 syncing 标记，防止 watch 循环（供 SDK 层使用） */
  const withSyncing = (fn: () => void) => {
    syncing = true
    fn()
    syncing = false
  }

  /** 从 SDK 数据填充 keyTravelMap（自定义模式） */
  const populateCustomKeys = (list: KeyTravelList) => {
    for (const item of list) {
      if (item.family !== 'classic') continue
      keyTravelMap.set(item.index, { ...item.keyTravel })
    }
  }

  /** 从 SDK 数据覆盖预设值 */
  const setPresetValues = (index: 0 | 1 | 2, values: PresetTravelValues) => {
    Object.assign(presetValues[index], values)
  }

  /** 将所有按键的 KeyTravel 应用预设值 */
  const applyPresetToAllKeys = (preset: PresetTravelValues) => {
    for (const [key, travel] of keyTravelMap) {
      keyTravelMap.set(key, {
        ...travel,
        rtTriggerEnable: true,
        rtTriggerSensitivity: preset.rtTriggerSensitivity,
        rtResetSensitivity: preset.rtResetSensitivity,
        bottomDeadZone: preset.bottomDeadZone,
      })
    }
  }

  /** 预设模式下将某个字段同步到所有按键 */
  const syncFieldToAllKeys = (
    field: keyof ClassicKeyTravel,
    value: ClassicKeyTravel[keyof ClassicKeyTravel],
  ) => {
    for (const [key, travel] of keyTravelMap) {
      keyTravelMap.set(key, { ...travel, [field]: value })
    }
  }

  const setSelectedKeys = (keys: { index: number }[]) => {
    selectedKeys.value = keys
  }

  /* -------- 计算属性 -------- */

  const hasSelectedKeys = computed(() => selectedKeys.value.length > 0)
  const isCustomMode = computed(() => rtSwitchIndex.value === 3)
  const isPresetMode = computed(() => rtSwitchIndex.value < 3)

  // 自定义模式下需要有选中按键才可操作
  const customNoSelection = computed(() => isCustomMode.value && !hasSelectedKeys.value)

  // RT switch 是否可用：自定义模式下需要有选中按键
  const rtSwitchDisabled = computed(() => customNoSelection.value)

  // 触发行程 slider 是否可用：自定义模式下需要有选中按键
  const triggerSliderDisabled = computed(() => customNoSelection.value)

  // 死区和灵敏度 slider 是否可用：需要 RT 开启，且（预设模式 或 有选中按键）
  const deadZoneEnabled = computed(
    () => editingState.rtTriggerEnable && (isPresetMode.value || hasSelectedKeys.value),
  )
  const sensitivityEnabled = computed(
    () => editingState.rtTriggerEnable && (isPresetMode.value || hasSelectedKeys.value),
  )

  // 对等模式按钮是否可用：仅自定义模式
  const relatedToggleEnabled = computed(() => isCustomMode.value)

  // 虚拟键盘右侧4按钮是否显示
  const showKeyboardActions = computed(() => !isPresetMode.value)

  // 键盘按键是否可选中
  const keySelectable = computed(() => !isPresetMode.value)

  // 预设模式下的标签文本（动态反映当前预设值）
  const presetLabels = computed(() => [
    `≈${(presetValues[0].rtTriggerSensitivity / 100).toFixed(1)}mm`,
    `≈${(presetValues[1].rtTriggerSensitivity / 100).toFixed(1)}mm`,
    `≈${(presetValues[2].rtTriggerSensitivity / 100).toFixed(1)}mm`,
  ])

  /* -------- 联动 watch -------- */

  // 1. 选中按键变化 → 加载第一个选中按键的状态；无选中时重置 editingState
  watch(selectedKeys, (keys) => {
    if (keys.length > 0) {
      loadEditingFromSelected()
    } else if (isCustomMode.value) {
      // 自定义模式下取消所有选中 → 关闭 RT，重置 editingState
      syncing = true
      editingState.rtTriggerEnable = false
      syncing = false
    }
  })

  // 2. editingState 变化 → 同步到所有选中按键（自定义模式），或同步到所有按键（预设模式）
  watch(
    editingState,
    () => {
      if (syncing) return
      if (isPresetMode.value) {
        // 预设模式下同步到所有按键
        for (const [key, travel] of keyTravelMap) {
          keyTravelMap.set(key, { ...travel, ...editingState })
        }
      } else {
        syncEditingToSelected()
      }
    },
    // 设备回填/选键加载都在 syncing 保护区内同步完成，避免异步 flush 后把聚合态反写到按键。
    { deep: true, flush: 'sync' },
  )

  // 3. rtSwitchIndex 切换（用户操作）；设备回填/switchRT 经 withSyncing 写入时跳过
  watch(rtSwitchIndex, (newIndex) => {
    if (syncing) return

    if (newIndex < 3) {
      // 切换到预设模式：自动开启 RT，应用预设值到所有按键，清空选中
      const preset = presetValues[newIndex]
      applyPresetToAllKeys(preset)
      selectedKeys.value = []

      // 更新 editingState 以反映预设值
      syncing = true
      editingState.rtTriggerEnable = true
      editingState.rtTriggerSensitivity = preset.rtTriggerSensitivity
      editingState.rtResetSensitivity = preset.rtResetSensitivity
      editingState.bottomDeadZone = preset.bottomDeadZone
      syncing = false

      // 预设模式强制对等
      isRelated.value = true
    } else {
      // 切换到自定义：只重置编辑态与选中。
      // 不要清空 keyTravelMap——设备回填前的 wipe 会导致切页回来 RT 丢失/残缺。
      syncing = true
      editingState.rtTriggerEnable = false
      editingState.rtTriggerSensitivity = DEFAULT_KEY_TRAVEL.rtTriggerSensitivity
      editingState.rtResetSensitivity = DEFAULT_KEY_TRAVEL.rtResetSensitivity
      editingState.bottomDeadZone = DEFAULT_KEY_TRAVEL.bottomDeadZone
      syncing = false

      selectedKeys.value = []
    }
  })

  // 4. 预设模式下 editingState 灵敏度变化 → 同步更新预设值
  watch(
    () => [
      editingState.rtTriggerSensitivity,
      editingState.rtResetSensitivity,
      editingState.bottomDeadZone,
    ],
    ([trigger, reset, deadZone]) => {
      if (syncing || !isPresetMode.value) return

      const preset = presetValues[rtSwitchIndex.value]
      preset.rtTriggerSensitivity = trigger as number
      preset.rtResetSensitivity = reset as number
      preset.bottomDeadZone = deadZone as number

      // 预设模式下同步到所有按键
      applyPresetToAllKeys(preset)
    },
  )

  // 5. 对等模式：rtResetSensitivity 跟随 rtTriggerSensitivity
  watch(
    () => editingState.rtTriggerSensitivity,
    (val) => {
      if (!isRelated.value) return
      if (syncing) return

      syncing = true
      editingState.rtResetSensitivity = val
      syncing = false
    },
  )

  watch(
    () => editingState.rtResetSensitivity,
    (val) => {
      if (!isRelated.value) return
      if (syncing) return

      syncing = true
      editingState.rtTriggerSensitivity = val
      syncing = false
    },
  )

  // 6. rtTriggerEnable 开启时：若仍是默认灵敏度则设为 0.50mm
  watch(
    () => editingState.rtTriggerEnable,
    (enabled) => {
      if (enabled) {
        if (
          editingState.rtTriggerSensitivity === DEFAULT_KEY_TRAVEL.rtTriggerSensitivity &&
          editingState.rtResetSensitivity === DEFAULT_KEY_TRAVEL.rtResetSensitivity
        ) {
          editingState.rtTriggerSensitivity = 50
          editingState.rtResetSensitivity = 50
        }
      }
    },
  )

  return {
    // 核心状态
    keyTravelMap,
    selectedKeys,
    editingState,
    rtSwitchIndex,
    isRelated,
    presetValues,

    // 计算属性
    hasSelectedKeys,
    isCustomMode,
    isPresetMode,
    rtSwitchDisabled,
    triggerSliderDisabled,
    deadZoneEnabled,
    sensitivityEnabled,
    relatedToggleEnabled,
    showKeyboardActions,
    keySelectable,
    presetLabels,

    // 方法
    getKeyTravel,
    setSelectedKeys,
    initAllKeys,
    syncFieldToAllKeys,

    // SDK 集成辅助
    withSyncing,
    populateCustomKeys,
    setPresetValues,
    applyPresetToAllKeys,
  }
}

export type UseItineraryModelReturn = ReturnType<typeof useItineraryModel>
